import React from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Avatar, avatarAltTextProvider, AvatarDetails } from '../../atoms/icons/Avatar'
import { ChevronV2Icon } from '../../atoms/icons/ChevronV2Icon'
import { TextBadge } from '../../atoms/icons/TextBadge'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { BadgeVariant } from '../../badge/constants'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderAvatarDetails = Omit<AvatarDetails, 'hide'>

export interface ProviderSessionCardProps {
  avatarDetails: ProviderAvatarDetails
  AvatarIllustration?: React.ReactNode
  backgroundColor?: string
  badgeText?: string
  label?: string
  labelColor?: string
  labelTextSize?: (typeof BodyTextSize)[keyof typeof BodyTextSize]
  sessionDescription?: string
  sessionInfo?: string | null
  sessionInfoColor?: string
  sessionInfoTextSize?: (typeof BodyTextSize)[keyof typeof BodyTextSize]
  testID?: string
  imageSize?: number
  providerID?: string
  withChevron?: boolean
  onPress?: ((isAppointmentInPast?: boolean | undefined) => void) | (() => void)
  withBorderStyle?: boolean
}

const SessionInfoContainer = styled.View({
  flexDirection: 'row',
  flex: '1',
  ...(!IS_WEB && { flexGrow: '1', flexBasis: 'auto' }),
})

const ProviderSessionCardContainer = styled(PressableOpacity)<{
  backgroundColor?: string
  theme: ThemeType
  withBorderStyle?: boolean
}>(({ backgroundColor, theme: { spacing, colors }, withBorderStyle }) => ({
  flexDirection: 'row',
  ...(!!backgroundColor && { backgroundColor }),
  ...(withBorderStyle && {
    padding: spacing['16px'],
    borderRadius: spacing['16px'],
    borderWidth: '1px',
    borderColor: colors.borderDefault,
  }),
}))

const AvatarContainer = styled.View({
  paddingRight: '12px',
  alignSelf: 'center',
})

const InformationContainer = styled.View({
  flexDirection: 'column',
  justifyContent: 'center',
  flexGrow: 1,
})

const BadgeContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignSelf: 'flex-start',
  marginBottom: spacing['8px'],
}))

const ChevronContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '24px',
  alignItems: 'center',
  marginLeft: spacing['16px'],
  justifyContent: 'center',
}))

export const ProviderSessionCard = ({
  avatarDetails: { src, displayName } = { src: '', displayName: '' },
  AvatarIllustration,
  backgroundColor,
  badgeText,
  label,
  labelColor,
  labelTextSize = BodyTextSize.SMALL,
  sessionDescription,
  sessionInfo,
  sessionInfoColor,
  sessionInfoTextSize = BodyTextSize.SMALL,
  testID,
  imageSize,
  providerID,
  withChevron,
  onPress,
  withBorderStyle,
}: ProviderSessionCardProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  return (
    <ProviderSessionCardContainer
      onPress={onPress && (() => onPress())}
      backgroundColor={backgroundColor}
      testID={tID(testID)}
      withBorderStyle={withBorderStyle}
    >
      <SessionInfoContainer>
        <AvatarContainer>
          {AvatarIllustration ? (
            AvatarIllustration
          ) : (
            <Avatar
              details={{ src, displayName }}
              accessibilityLabel={formatMessage(avatarAltTextProvider, { name: displayName })}
              size={imageSize}
              providerID={providerID}
            />
          )}
        </AvatarContainer>
        <InformationContainer>
          {badgeText && (
            <BadgeContainer>
              <TextBadge
                testID={tID('ProviderSessionCard-textBadge')}
                text={badgeText}
                variant={BadgeVariant.INFO_SECONDARY}
                preserveCase
                bold
              />
            </BadgeContainer>
          )}
          {label ? (
            <BodyText text={label} size={labelTextSize} color={labelColor || colors.textPrimary} />
          ) : (
            <Title text={displayName} size={TitleSize.XSMALL} numberOfLines={2} color={colors.textPrimary} noRole />
          )}
          {!!sessionDescription && (
            <BodyText text={sessionDescription} size={BodyTextSize.DEFAULT} color={colors.textPrimary} />
          )}
          {!!sessionInfo && (
            <BodyText
              text={sessionInfo}
              size={sessionInfoTextSize}
              numberOfLines={2}
              color={sessionInfoColor || colors.iconDefault}
              testID={tID('ProviderSessionCard-sessionInfo')}
            />
          )}
        </InformationContainer>
      </SessionInfoContainer>
      {withChevron && onPress && (
        <ChevronContainer>
          <ChevronV2Icon fillColor={colors.iconDefault} size={12} />
        </ChevronContainer>
      )}
    </ProviderSessionCardContainer>
  )
}
